import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'

const PageWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`

const Header = styled.div`
    padding: 1rem 1.5rem 0.75rem;
    border-bottom: 1px solid ${p => p.theme.form.border};
    flex-shrink: 0;
`

const Title = styled.h2`
    font-size: 1.1rem;
    font-weight: 600;
    color: ${p => p.theme.font.color};
    margin: 0;
`

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem 1.5rem;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
`

const Th = styled.th`
    text-align: left;
    padding: 0.5rem 0.75rem;
    color: ${p => p.theme.font.label};
    font-weight: 600;
    border-bottom: 1px solid ${p => p.theme.form.border};
`

const Td = styled.td`
    padding: 0.4rem 0.75rem;
    color: ${p => p.theme.font.color};
    border-bottom: 1px solid ${p => p.theme.form.border};
`

const TotalRow = styled.tr`
    font-weight: 700;
    border-top: 2px solid ${p => p.theme.form.border};
`

// Phase-1-simple: all-time balance per account, no date-range filter — see
// ledgerEntry.supabaseAggregations (entities.js) for the underlying SQL.
const AccountBalancePage = () => {
    const { t } = useTranslation()
    const { getMe } = useApi()

    const { data, isLoading } = useQuery({
        queryKey: ['ledgerEntry', 'byAccount'],
        queryFn: () => getMe('ledgerEntry/agr/byAccount'),
    })

    const { data: accountsData } = useQuery({
        queryKey: ['glAccount', 'all'],
        queryFn: () => getMe('glAccount?limit=1000'),
        staleTime: 5 * 60 * 1000,
    })

    const accountMap = Object.fromEntries(
        (accountsData?.data || []).map(a => [a.id, `${a.number} — ${a.name}`])
    )

    const rows = (Array.isArray(data?.data) ? data.data : [])
        .filter(r => r.balance !== 0 && r.balance !== null)
        .sort((a, b) => (accountMap[a.accountid] || a.accountid).localeCompare(accountMap[b.accountid] || b.accountid))

    const total = rows.reduce((sum, r) => sum + (r.balance || 0), 0)

    return (
        <PageWrap>
            <Header>
                <Title>{t('accounting.balance.title')}</Title>
            </Header>
            <Body>
                {isLoading ? (
                    <div>…</div>
                ) : rows.length === 0 ? (
                    <div>{t('accounting.balance.empty')}</div>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <Th>{t('accounting.balance.table.account')}</Th>
                                <Th style={{ textAlign: 'right' }}>{t('accounting.balance.table.balance')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.accountid}>
                                    <Td>{accountMap[row.accountid] || row.accountid}</Td>
                                    <Td style={{ textAlign: 'right' }}>{Number(row.balance).toFixed(2)}</Td>
                                </tr>
                            ))}
                            <TotalRow>
                                <Td>{t('accounting.balance.table.total')}</Td>
                                <Td style={{ textAlign: 'right' }}>{total.toFixed(2)}</Td>
                            </TotalRow>
                        </tbody>
                    </Table>
                )}
            </Body>
        </PageWrap>
    )
}

export default AccountBalancePage
